import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Logo } from '../../design-system';
import { ApiError } from '../../lib/apiClient';
import { authApi } from './auth.api';

/**
 * Je adres invullen om een herstellink te krijgen. Zelfde opzet als het
 * inlogscherm: merk gecentreerd boven de kaart, kop erin links.
 *
 * Na verzenden komt er één bevestiging, en die zegt niet of het adres bestaat.
 * Dat is geen slordigheid maar het hele punt: was het antwoord verschillend, dan
 * is dit formulier een manier om uit te zoeken wie een account heeft. Het scherm
 * gaat daarom naar een bevestiging in plaats van het formulier te laten staan
 * met een melding erboven — dan is er ook niets om op te vallen.
 */
export function WachtwoordVergetenPage() {
  const [email, setEmail] = useState('');
  const [verstuurd, setVerstuurd] = useState(false);
  const [fout, setFout] = useState('');
  const [bezig, setBezig] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFout('');
    setBezig(true);
    try {
      await authApi.vraagHerstel(email);
      setVerstuurd(true);
    } catch (err) {
      // Vrijwel alleen de rem uit `herstelAanvraagKeys` komt hier terecht; de
      // server geeft verder altijd `ok`.
      setFout(err instanceof ApiError ? err.message : 'Er ging iets mis. Probeer het opnieuw.');
    } finally {
      setBezig(false);
    }
  }

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted p-6 md:p-10">
      <div className="flex w-full max-w-sm flex-col gap-6">
        <span className="self-center">
          <Logo height={40} />
        </span>

        {verstuurd ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Kijk in je mail.</CardTitle>
              <CardDescription>
                Hoort <strong>{email}</strong> bij een account, dan staat er nu een link in de
                mailbox om een nieuw wachtwoord te kiezen.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              De link werkt een uur. Geen mail gekregen? Controleer het adres en de map met
              ongewenste mail.
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Wachtwoord vergeten</CardTitle>
              <CardDescription>
                Vul je e-mailadres in, dan sturen we je een link om een nieuw wachtwoord te kiezen.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={onSubmit}>
                <FieldGroup>
                  <Field data-invalid={fout ? true : undefined}>
                    <FieldLabel htmlFor="email">E-mailadres</FieldLabel>
                    <Input
                      aria-invalid={fout ? true : undefined}
                      autoComplete="email"
                      id="email"
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      type="email"
                      value={email}
                    />
                    {fout && <FieldError>{fout}</FieldError>}
                  </Field>

                  <Field>
                    <Button disabled={bezig} type="submit">
                      {bezig && <Spinner data-icon="inline-start" />}
                      Link versturen
                    </Button>
                  </Field>
                </FieldGroup>
              </form>
            </CardContent>
          </Card>
        )}

        <Link className="self-center text-sm underline-offset-4 hover:underline" to="/login">
          Terug naar inloggen
        </Link>
      </div>
    </div>
  );
}
